import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { VBox } from "@ui/box/Box";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import Input from "@ui/input/Input";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import Decimal from "decimal.js";
import Signal from "badmfck-signal";
import { getNotificationErrorMessage, notify } from "src/tools/notify";

export const S_REFUND_POPUP = new Signal<{ amount: number; sessionId: string | number; method: string; currency: string } | null>("S_REFUND_POPUP");

export const RefundPopup = () => {
	useLocale();
	const data = S_REFUND_POPUP.use();

	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();
	const [formData, setFormData] = useState<{ amount: string }>({ amount: "" });

	useEffect(() => {
		if (data) {
			setFormData({ amount: new Decimal(data.amount).dividedBy(100).toString() });
			setError(undefined);
		}
	}, [data]);

	if (!data) return null;

	const maxAmount = new Decimal(data.amount).dividedBy(100);

	const closePopup = () => {
		setBusy(false);
		setError(undefined);
		setFormData({ amount: "" });
		S_REFUND_POPUP.invoke(null);
	};

	const handleRefund = async () => {
		setError(undefined);

		let enteredAmount: Decimal;
		try {
			enteredAmount = new Decimal(formData.amount || "0");
		} catch (error) {
			setError({ code: 0, message: msg("invalidAmount") });
			notify.error(getNotificationErrorMessage(error, msg("invalidAmount")));
			return;
		}

		if (enteredAmount.lte(0)) {
			setError({ code: 0, message: msg("amountMustBeGreaterThan0") });
			notify.error(msg("amountMustBeGreaterThan0"));
			return;
		}
		if (enteredAmount.gt(maxAmount)) {
			setError({ code: 0, message: msg("amountCannotExceed") + maxAmount.toString() });
			notify.error(msg("amountCannotExceed") + maxAmount.toString());
			return;
		}

		setBusy(true);
		try {
			const amount = enteredAmount.times(100).toNumber();
			const res = await GD.REQ_API.request({
				method: data.method,
				data: {
					session_id: data.sessionId,
					amount: amount < data.amount ? amount : undefined,
				},
			});

			if (res.error) {
				setError(res.error);
				notify.error(getNotificationErrorMessage(res.error, msg("refundFailed")));
			} else {
				notify.success(msg("refundCreated"));
				GD.S_TRANSACTIONS_UPDATE.invoke();
				closePopup();
			}
		} catch (error) {
			const message = getNotificationErrorMessage(error, msg("refundFailed"));
			setError({ code: 0, message });
			notify.error(message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<Panel
			sx={{ maxWidth: "480px", minWidth: "360px", width: "95vw" }}
			scrollable
			decorator="absolute"
			onClose={closePopup}
			growContent
			label={msg("makeRefund")}
			busy={busy}>

			<Error error={error} />

			{error && <Spacer size="20px" />}

			<VBox gap="4px">
				<Typo variant="caption" color="textSecondary">{msg("maxAmount")}{maxAmount.toString()}
				</Typo>
				<Input
					name="amount"
					label={msg("amount")}
					formatAmount
					units={data.currency}
					type="text"
					placeholder={"0.00"}
					data={formData}
					onChange={v => setFormData(prev => ({ ...prev, amount: v }))}
				/>
			</VBox>

			<Spacer size="20px" />

			<Button onClick={handleRefund} title={msg("makeRefund")} variant="primary" />
		</Panel>
	);
};
